import React, { useMemo } from 'react';
import { Pressable, ScrollView, Text } from 'react-native';
import { colors, radius, spacing, type, useAppTheme } from '@/theme';
import { localDayISO } from '@/utils/date';
import { ScaledSheet } from '@/theme/responsive';

interface DayOption {
  iso: string;
  weekday: string;
  dayNum: string;
  label: string;
  disabled: boolean;
}

function buildDays(count: number, unavailableIsoList: string[] = []): DayOption[] {
  const days: DayOption[] = [];
  const today = new Date();
  for (let i = 0; i < count; i++) {
    const d = new Date(today);
    d.setDate(d.getDate() + i);
    const iso = localDayISO(i);
    days.push({
      iso,
      weekday: d.toLocaleDateString('en-US', { weekday: 'short' }),
      dayNum: String(d.getDate()),
      label: i === 0 ? 'Today' : i === 1 ? 'Tomorrow' : d.toLocaleDateString('en-US', { month: 'short' }),
      disabled: unavailableIsoList.includes(iso),
    });
  }
  return days;
}

interface Props {
  selected: string | null;
  onSelect: (iso: string) => void;
  daysToShow?: number;
  unavailableIsoList?: string[];
}

export function DateSelector({ selected, onSelect, daysToShow = 14, unavailableIsoList }: Props) {
  const { colors } = useAppTheme();
  const days = useMemo(() => buildDays(daysToShow, unavailableIsoList), [daysToShow, unavailableIsoList]);

  return (
    <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={styles.row}>
      {days.map((day) => {
        const isSelected = day.iso === selected;
        return (
          <Pressable
            key={day.iso}
            disabled={day.disabled}
            accessibilityRole="button"
            accessibilityState={{ selected: isSelected, disabled: day.disabled }}
            onPress={() => onSelect(day.iso)}
            style={[
              styles.cell,
              { backgroundColor: colors.surface, borderColor: colors.border },
              isSelected && styles.cellSelected,
              day.disabled && styles.cellDisabled,
            ]}>
            <Text style={[type.caption, styles.weekday, { color: colors.textTertiary }, isSelected && styles.textSelected]}>{day.weekday}</Text>
            <Text style={[type.h3, styles.dayNum, { color: colors.textPrimary }, isSelected && styles.textSelected, day.disabled && styles.textDisabled]}>{day.dayNum}</Text>
            <Text numberOfLines={1} adjustsFontSizeToFit minimumFontScale={0.8} style={[type.caption, styles.label, { color: colors.textTertiary }, isSelected && styles.textSelected]}>{day.label}</Text>
          </Pressable>
        );
      })}
    </ScrollView>
  );
}

const styles = ScaledSheet.create({
  row: { gap: spacing.sm, paddingVertical: 2 },
  cell: {
    width: 62,
    paddingHorizontal: 4,
    paddingVertical: spacing.sm,
    borderRadius: radius.md,
    borderWidth: 1,
    borderColor: colors.border,
    backgroundColor: colors.surface,
    alignItems: 'center',
    gap: 2,
  },
  cellSelected: { backgroundColor: colors.accentFill, borderColor: colors.accent },
  cellDisabled: { opacity: 0.4 },
  weekday: { color: colors.textTertiary },
  dayNum: { color: colors.textPrimary },
  label: { color: colors.textTertiary },
  textSelected: { color: colors.textOnAccent },
  textDisabled: { color: colors.textTertiary },
});
